/**
 * customerMatch.js - tying CRM accounts to opportunity / project rows.
 *
 * Accounts and projects name the same customer differently ("FANUC America -
 * PSH" vs "FANUC - PSH"), so both sides are reduced to a sorted token set with
 * the noise words stripped, and the sets must match EXACTLY - that is what
 * stops a "FANUC" account swallowing every FANUC - ACIP job.
 *
 * Used by: views/CRMView (accounts list KPIs), components/crm/AccountDetailPanel
 * (one account's opportunities + live Won/Open/Lost) and components/crm/leadsBoard
 * (the Leads card "Opps: X open / Y closed" line, via customerKey).
 */
import { isCancelledStatus } from '../data/projectStatus';

const CUST_NOISE = new Set(['america', 'corp', 'corporation', 'inc', 'incorporated', 'llc', 'ltd', 'limited', 'the', 'of', 'and', 'co']);
function customerTokens(name) {
  return (name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .split(/\s+/)
    .filter((w) => w.length >= 2 && !CUST_NOISE.has(w))
    .sort();
}

/**
 * The key matchCustomer compares, as one string ('' when only noise is left).
 * Two names match exactly when their keys are equal and non-empty, so a caller
 * can index thousands of rows once instead of comparing every pair.
 */
export function customerKey(name) {
  return customerTokens(name).join(' ');
}

/**
 * computeAccountStats — live KPI roll-up for ALL accounts from allProjects.
 *
 * Returns a Map keyed by account id with { won, open, lost, cancelled,
 * noQuote, wonValue, pipelineValue }. noQuote (Paul, Q5: "we backed out") is
 * its own bucket so the older fields keep their meaning. The counts are never
 * stored: they are worked out from the opportunities on every render, and the
 * live updates re-read those the moment anyone saves one. GET /api/accounts
 * sends the same numbers (server/lib/accountLink.js accountStats, the same
 * rules) for readers without the projects list.
 *
 * A row linked to a known account (projects.account_id, A-02 - the exact
 * site; since 10/11/26 every opportunity has one) counts on THAT account and
 * skips the name match; only unlinked rows (or links to an account not in the
 * list) fall back to matching the name - a company before a site, then the
 * oldest, so two records answering to one name never split the work.
 */
export function computeAccountStats(allAccounts, allProjects) {
  const stats = new Map();
  for (const acc of allAccounts) {
    stats.set(acc.id, { won: 0, open: 0, lost: 0, cancelled: 0, noQuote: 0, wonValue: 0, pipelineValue: 0 });
  }
  // One index of name keys -> the account that wins that name
  const isSite = (a) => (a.parentAccountId || a.parent_account_id ? 1 : 0);
  const when = (a) => {
    const t = Date.parse(a.createdAt || a.created_at || '');
    return Number.isFinite(t) ? t : Infinity;
  };
  const ordered = [...allAccounts].sort((x, y) => isSite(x) - isSite(y) || when(x) - when(y) || String(x.id).localeCompare(String(y.id)));
  const byKey = new Map();
  for (const a of ordered) {
    for (const k of [customerKey(a.opportunityKey || a.opportunity_key), customerKey(a.name)]) {
      if (k && !byKey.has(k)) byKey.set(k, a.id);
    }
  }
  const byName = (cust) => {
    const k = customerKey(cust);
    return k && byKey.has(k) ? stats.get(byKey.get(k)) : null;
  };
  for (const p of allProjects || []) {
    const linked = p.accountId || p.account_id;
    const bucket = (linked && stats.get(linked)) || byName(p.customer);
    if (!bucket) continue;
    const s = (p.status || '').toLowerCase();
    const v = parseFloat(p.quotedPrice || p.quoted_price || 0);
    const valid = isFinite(v) && v < 500000000 ? v : 0;
    if (s === 'won') {
      bucket.won++;
      bucket.wonValue += valid;
    } else if (s === 'open') {
      bucket.open++;
      bucket.pipelineValue += valid;
    } else if (s === 'lost') bucket.lost++;
    else if (isCancelledStatus(s))
      bucket.cancelled++; // Q-01: Cancelled (was Dead 9/29-10/1/26)
    else if (s === 'no quote') bucket.noQuote++;
  }
  // one rollup rule: a Site's numbers also count on its parent, so a corporate
  // account shows its whole pipeline (Paul #9 - Karma showed Open 0 while its
  // only plant held the $700K quote)
  for (const acc of allAccounts) {
    const pid = acc.parentAccountId || acc.parent_account_id;
    if (!pid) continue;
    const parent = stats.get(pid),
      own = stats.get(acc.id);
    if (parent && own) for (const k of Object.keys(own)) parent[k] += own[k];
  }
  return stats;
}

export function matchCustomer(accountName, itemCustomer) {
  // Compare token-sets after stripping punctuation, case, and noise words.
  // - "FANUC America - PSH"  → ["fanuc","psh"]
  // - "FANUC - PSH"          → ["fanuc","psh"]    ✓ match
  // - "FANUC - ACIP"         → ["acip","fanuc"]   ✗ no match with PSH
  // Both arrays must be equal so a "FANUC" account doesn't bleed into ACIP.
  if (!accountName || !itemCustomer) return false;
  const a = customerTokens(accountName);
  const b = customerTokens(itemCustomer);
  if (a.length !== b.length || a.length === 0) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}
